import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, ScrollView, View } from 'react-native';

import ClosingBand from '@/components/Landing/ClosingBand';
import FlowField from '@/components/Landing/FlowField';
import {
  DisplayHeading,
  Kicker,
  Lede,
  LandingThemeProvider,
  Section,
  useInteractive,
  useLanding,
} from '@/components/Landing/LandingKit';
import SemanticBlock from '@/components/Landing/SemanticBlock';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import { EASE, LAYOUT, fluid, isWeb } from '@/components/Landing/landingTokens';
import UseCaseDirectoryCard from '@/components/Pages/UseCaseDirectoryCard.web';
import Reveal from '@/components/Landing/Reveal';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Text from '@/components/Typography/Text';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import USE_CASE_HUB_CATEGORIES from '@/constants/useCaseHubCategories';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '@/hooks/useUseCaseMenu';
import { useCasesApi } from '@/lib/api/useCases';
import { isUseCaseDiscoverable } from '../../../../shared/public/use-cases.cjs';
import { markAppReady } from '@/lib/appReady';

function FilterPill({ label, isActive, onPress }) {
  const { c } = useLanding();
  const { hovered, handlers } = useInteractive();

  return (
    <Pressable
      {...handlers}
      onPress={onPress}
      accessibilityRole="button"
      aria-pressed={isActive}
      style={{
        paddingHorizontal: 16,
        paddingVertical: 9,
        borderRadius: 9999,
        borderWidth: 1,
        borderColor: isActive
          ? c.accentRing
          : (hovered ? c.ruleStrong : c.rule),
        backgroundColor: isActive
          ? (hovered ? c.accentSoft : c.accentWash)
          : (hovered ? c.panel : 'transparent'),
        ...(isWeb ? { cursor: 'pointer', transition: `background-color 180ms ${EASE}, border-color 180ms ${EASE}` } : {}),
      }}
    >
      <Text
        tag="span"
        weight={isActive ? 'bold' : 'medium'}
        style={{ color: isActive ? c.accent : c.ink, fontSize: 13.5 }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function UseCaseGroup({ label, items }) {
  const { c, width, isXS } = useLanding();
  if (!items.length) return null;
  const columns = width >= 1100 ? 3 : width >= 700 ? 2 : 1;
  return <SemanticBlock tag="section" aria-label={label} style={{ width: '100%', gap: isXS ? 22 : 26 }}>
    <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: isXS ? 24 : 28,
      lineHeight: 34, letterSpacing: -0.7 }}>{label}</Text>
    <SemanticBlock style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      gap: isXS ? 20 : 24, alignItems: 'stretch' }}>
      {items.map(item => <UseCaseDirectoryCard key={item.slug} data={item} />)}
    </SemanticBlock>
  </SemanticBlock>;
}

export default function UseCasesIndexPage() {
  return <LandingThemeProvider><UseCasesDirectory /></LandingThemeProvider>;
}

function UseCasesDirectory() {
  const { colors, theme } = useTheme();
  const { c, isXS, width, display } = useLanding();
  const isDark = isDarkTheme(theme);
  const [activeCategory, setActiveCategory] = useState('all');
  const [useCases, setUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const headline = display({ min: 36, max: 68, leading: 0.98, tracking: -0.042 });

  useLandingStyles();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const data = await useCasesApi.list({ summary: true });
        if (!alive) return;
        setUseCases(Array.isArray(data?.useCases) ? data.useCases.filter(isUseCaseDiscoverable) : []);
      } catch {
        if (!alive) return;
        setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [retry]);

  useEffect(() => {
    if (!loading && !error && useCases.length) markAppReady();
  }, [loading, error, useCases]);

  const filteredUseCases = useMemo(() => {
    if (activeCategory === 'all') return useCases;
    return useCases.filter((item) => item.hubCategory === activeCategory);
  }, [activeCategory, useCases]);

  const groups = useMemo(() => {
    if (activeCategory !== 'all') {
      const match = USE_CASE_HUB_CATEGORIES.find((cat) => cat.value === activeCategory);
      return [{
        key: activeCategory,
        label: match?.label || CATEGORY_LABELS[activeCategory] || activeCategory,
        items: filteredUseCases,
      }];
    }

    const byCategory = new Map();
    for (const item of useCases) {
      const key = item.hubCategory || 'business';
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key).push(item);
    }

    const grouped = [];
    for (const key of CATEGORY_ORDER) {
      const items = byCategory.get(key);
      if (!items?.length) continue;
      grouped.push({
        key,
        label: CATEGORY_LABELS[key] || key,
        items,
      });
    }
    return grouped;
  }, [activeCategory, filteredUseCases, useCases]);

  const containerStyle = useMemo(() => ({
    flex: 1,
    backgroundColor: colors.solid_color_primary,
  }), [colors]);

  return (
    <View style={containerStyle}>
      <FlowField isDark={isDark} />
      <MetaHead
        title="Gab AI Use Cases: AI Tools for Every Profession"
        description="Explore AI use cases for writers, marketers, developers, teachers, homeschoolers, sales teams, designers, and more. One platform, every creative and business tool you need."
        canonical="/use-cases"
        keywords="AI use cases, AI tools, AI for business, AI for creators, AI for developers, AI for homeschoolers, Gab AI"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Gab AI Use Cases: AI Tools for Every Profession',
          url: 'https://gab.ai/use-cases',
          mainEntity: { '@type': 'ItemList', itemListElement: useCases.map((item, index) => ({
            '@type': 'ListItem', position: index + 1, name: item.name,
            url: `https://gab.ai/use-cases/${item.slug}`,
          })) },
        }}
      />

      <ScrollView
        {...(Platform.OS === 'web' ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole="main"
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <Section
          label="AI use cases by profession"
          top={false}
          contentStyle={{
            paddingTop: fluid(width, 104, 152),
            paddingBottom: fluid(width, 28, 40),
          }}
        >
          <View style={{ maxWidth: 760 }}>
            <Kicker>Use cases</Kicker>
            <DisplayHeading
              tag="h1"
              mask={false}
              type={headline}
              style={{ marginTop: 22 }}
              lines={['Built for how you work']}
            />
            <Lede max={520} style={{ marginTop: 18 }}>
              One AI platform, every tool you need. See how Gab AI is purpose-built for your profession.
            </Lede>
          </View>
        </Section>

        <SemanticBlock
          tag="nav"
          aria-label="Filter use cases by category"
          style={{
            width: '100%',
            maxWidth: LAYOUT.max,
            paddingLeft: fluid(width, 20, 64),
            paddingRight: fluid(width, 20, 64),
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: fluid(width, 36, 52),
          }}
        >
          {USE_CASE_HUB_CATEGORIES.map((category) => (
            <FilterPill
              key={category.value}
              label={category.label}
              isActive={activeCategory === category.value}
              onPress={() => setActiveCategory(category.value)}
            />
          ))}
        </SemanticBlock>

        <SemanticBlock
          tag="section"
          aria-label="All use cases"
          style={{
            width: '100%',
            maxWidth: LAYOUT.max,
            paddingLeft: fluid(width, 20, 64),
            paddingRight: fluid(width, 20, 64),
            paddingBottom: fluid(width, 48, 72),
            minHeight: 200,
            alignItems: loading ? 'center' : undefined,
            justifyContent: loading ? 'center' : undefined,
          }}
        >
          {loading ? (
            <ActivityIndicator size="large" color={c.muted} />
          ) : error ? (
            <View style={{ gap: 16 }}>
              <Text tag="p" style={{ color: c.muted }}>We couldn’t load the use cases. Please try again.</Text>
              <Pressable accessibilityRole="button" onPress={() => setRetry(value => value + 1)}>
                <Text style={{ color: c.ink }}>Try again</Text>
              </Pressable>
            </View>
          ) : (
            <Reveal>
              <View style={{ gap: isXS ? 36 : 48 }}>
                {groups.map((group) => (
                  <UseCaseGroup key={group.key} label={group.label} items={group.items} />
                ))}
              </View>
            </Reveal>
          )}
        </SemanticBlock>

        <ClosingBand
          kicker="Don't see your industry?"
          lines={['Gab AI works for everyone']}
          lede="Try it free and discover what AI can do for your work. Same account, same credits, every model."
          primary={{ label: 'Try Gab AI free', href: '/signup' }}
          secondary={{ label: 'Compare plans and pricing', href: '/pricing' }}
          trust={['No credit card required', 'Cancel anytime']}
        />

        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
